// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import { ISO_3166_1_ALPHA_2, resolveCountryCode } from '@vn-grc/shared';

export interface CountryOption {
  /** The ISO 3166-1 alpha-2 code — the only thing the API accepts. */
  value: string;
  /** The country's name in the reader's locale. */
  label: string;
}

/**
 * The country choices an intake form may offer, named in the reader's locale.
 *
 * Both rights-request forms sent `country` to a schema that demands exactly two
 * upper-case characters while offering a free-text box labelled "Country /
 * Jurisdiction" — copy that invites "Vietnam". The control could not produce a
 * value its own endpoint accepted, so an operator typing a country name got a
 * `400 validation_error` in English with nothing on the form to explain it.
 * Every option built here parses; that is the point of the helper.
 *
 * Names come from `Intl.DisplayNames` rather than from 249 × 16 locale strings,
 * so a Vietnamese reader gets "Việt Nam" and an English one "Vietnam" without
 * either list being hand-authored or drifting. Where the platform locale has no
 * region data — an unusual runtime, or a locale ICU does not carry — the code
 * itself is the label, which is still a correct, submittable choice.
 *
 * Sorted by the LOCALISED name, so the list reads alphabetically to whoever is
 * looking at it rather than to an English speaker only.
 */
export function buildCountryOptions(locale: string): CountryOption[] {
  let display: Intl.DisplayNames | undefined;
  try {
    display = new Intl.DisplayNames([locale], { type: 'region' });
  } catch {
    display = undefined;
  }

  const options: CountryOption[] = ISO_3166_1_ALPHA_2.map((code) => {
    let label: string = code;
    try {
      label = display?.of(code) ?? code;
    } catch {
      label = code;
    }
    return { value: code, label };
  });

  const collator = (() => {
    try {
      return new Intl.Collator(locale);
    } catch {
      return new Intl.Collator();
    }
  })();

  return options.sort((a, b) => collator.compare(a.label, b.label));
}

/**
 * fix-sprint #431 (gate E12.5 remediation 1) — the organisation's country as the
 * cross-border rule reads it, for a form to show.
 *
 * `resolveOrgCountry` on the server reads the record's own `country` field
 * first and a `metadata.country` written through the API before that field
 * existed second, each read as an ISO 3166-1 alpha-2 code. An edit form that
 * read `country` alone said "Not recorded" over an organisation the rule was
 * comparing as recorded. This reads the same two places in the same order, so
 * what the form shows is what the rule uses. Returns `''` (not recorded) when
 * neither resolves to a code.
 */
export function recordedOrgCountry(org: { country?: unknown; metadata?: unknown }): string {
  const fromField = resolveCountryCode(org.country);
  if (fromField.status === 'resolved') return fromField.code;
  const meta =
    org.metadata && typeof org.metadata === 'object'
      ? (org.metadata as Record<string, unknown>)
      : {};
  const fromMeta = resolveCountryCode(meta.country);
  if (fromMeta.status === 'resolved') return fromMeta.code;
  return '';
}
